/**
 * Composing a Meta ad — a new draft, an edit of one nothing has been created
 * from yet, or (slice 52) a correction of one a publish stopped part way.
 *
 * ## THE CORRECTION IS THE SAME PAGE, WITH WHAT EXISTS AT META CLOSED
 *
 * Until slice 52 a draft whose campaign had been created at Meta could not be
 * opened here at all, so the commonest real refusal — a budget under Meta's
 * minimum for the currency — could only be answered with a new draft beside a
 * campaign already created and recorded nowhere else. Now the page opens, and
 * `frozenAtMeta` decides section by section what may change: a campaign id
 * closes the campaign, an ad set id closes the ad set and the Page, a creative
 * closes the Page and the Instagram account, and an ad with any of a video, a
 * creative or an ad id is closed whole. Each closed section carries one "At
 * Meta" badge in its header.
 *
 * **The closure is drawn so that the refusal never has to be read.** The API
 * refuses a correction to a frozen field on the field itself, and that refusal
 * is a backstop, not the design: a person who can type into a box and is then
 * told they may not has been invited to waste the attempt. So the box is shut,
 * and the badge says why in two words.
 *
 * **And the list of ads may only grow.** `published` holds three arrays
 * positional against `draft.ads`, so a removal or a reorder would leave an
 * ad's recorded creative belonging to a different ad — see the comment above
 * the ads.
 *
 * ## NOTHING ON THIS PAGE REACHES META
 *
 * Saving stores a draft in Wend. Publishing is a separate act on the draft's own
 * page, with the version named — see `MetaAdDraftPage`. So this page can be
 * used freely while App Review is still pending: composing needs only a
 * connection whose check succeeded, for the currency the budget is typed in.
 *
 * ## LABELS AND INPUTS, AND THE PLACES THE PAGE SPEAKS
 *
 * The rule for this programme is no explanatory text. The page speaks only
 * where a person would otherwise make a mistake that costs them later:
 *
 *   - the **currency** beside the budget, because "2000" means nothing without
 *     it and the account decides it, not the person. It travels with the
 *     amount, and the API refuses an amount typed against a currency the
 *     account no longer reports in. When the account now reports in a
 *     different currency from the one a draft was saved in, the amount starts
 *     empty and the old budget sits under it as money in its own currency —
 *     see `formForAccount` for what carrying the number across would cost;
 *   - the **special category** starts unchosen and the form will not save until
 *     it is chosen — a declaration the bank makes, never a default;
 *   - when a restricted category is chosen, the age and gender fields **change
 *     in front of the person** and lock, with a badge saying why — scrolled
 *     into view, because on a laptop the fields that changed are below the
 *     select that changed them.
 *
 * Slice 47 adds where the ads lead — a website or a WhatsApp chat — and
 * WhatsApp Status among the placements, with no sentence for either: choosing
 * a chat sets the objective to Traffic and greys out Awareness and Carousel,
 * and an ad's link and button fields go, because Meta fixes both for a chat
 * ad; WhatsApp Status can be ticked only for a chat, and ticks Instagram with
 * it. Each change happens in the fields the person is looking at.
 *
 * ## A SAVE THAT IS REFUSED SAYS SO WHERE THE PERSON IS LOOKING
 *
 * Save is at the bottom of a long form, and most refusals belong to a field
 * far above it. After any refusal — the form's own or the API's — focus moves
 * to the first field marked invalid, which scrolls it into view and reads its
 * message. The API's field refusals are shown on their fields and nowhere
 * else: the same sentence at the top, in a second alert and under the field
 * was one refusal said three times.
 *
 * All the logic lives in `meta-ad-draft-form.ts`, which a suite tests without
 * rendering this page.
 */
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  FormField,
  Input,
  Select,
  Spinner,
  StateChip,
  ValueChip,
  buttonVariants,
} from '@wend/ui';
import { ArrowLeft, Lock, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  META_AD_OBJECTIVE_VALUES,
  META_CTA_VALUES,
  META_DRAFT_MAX_ADS,
  META_SPECIAL_AD_CATEGORY_VALUES,
  type MetaAdsIntegration,
  type MetaSpecialAdCategory,
} from '@wend/shared';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { Textarea } from '../../components/Textarea';
import { ROOT_FIELD, apiFieldErrors } from '../../lib/api/errors';
import {
  createMetaAdDraft,
  fetchMetaAdDraft,
  fetchMetaAdsIntegration,
  updateMetaAdDraft,
} from '../../lib/api/meta-ads';
import { useAsyncData } from '../../lib/use-async-data';
import { integrationTabPath } from '../integrations/integration-tab-url';
import { formatMetaMoney } from '../integrations/MetaAdsAccountRead';
import { RequestFailureAlert, requestFailure, type RequestFailure } from '../integrations/request-failure';
import {
  BUDGET_KIND_LABELS,
  CATEGORY_FIELD,
  emptyAd,
  countryCodes,
  emptyCard,
  emptyDraftForm,
  fieldFor,
  formForAccount,
  formFromDraft,
  frozenAtMeta,
  frozenAtMetaNothing,
  isRestricted,
  requestFromForm,
  withCategory,
  withDestination,
  withInstagram,
  withStatus,
  type AdForm,
  type CreativeFormat,
  type DraftForm,
  type FrozenAtMeta,
} from './meta-ad-draft-form';
import {
  CATEGORY_LABEL,
  CTA_LABEL,
  DESTINATION_LABEL,
  DraftStatusBadge,
  FORMAT_LABEL,
  Fact,
  Mono,
  OBJECTIVE_LABEL,
  isCorrectable,
  isEditable,
} from './MetaAdDraftSummary';

/** Today in the ad account's zone, as `YYYY-MM-DD` — the earliest end date the API takes. */
function todayIn(timezone: string): string | undefined {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return undefined;
  }
}

export function MetaAdComposerPage() {
  const { draftId } = useParams<{ draftId?: string }>();
  const editing = draftId !== undefined;

  const loaded = useAsyncData(
    async (signal) => ({
      integration: await fetchMetaAdsIntegration(signal),
      record: editing ? await fetchMetaAdDraft(draftId, signal) : null,
    }),
    [draftId],
  );

  /* Slice 52: the same route composes, edits and corrects; which of the three
     is read off the record's own `published`, so a link never has to know. The
     title says which before the form does — and while the record is still
     loading it says "Edit", because a page that opened as "Correct Meta ad"
     and settled into an ordinary edit would have announced a campaign at Meta
     that does not exist. */
  const record = loaded.data?.record ?? null;
  const correcting = record !== null && isCorrectable(record, Date.now());
  const title = !editing ? 'New Meta ad' : correcting ? 'Correct Meta ad' : 'Edit Meta ad';

  if (loaded.data === null) {
    return (
      <AppLayout title={title} actions={<BackToMetaAds />}>
        <Card>
          {loaded.loadingFirst ? (
            <CardContent className="flex items-center gap-3 p-[18px] text-[0.92rem] text-muted">
              <Spinner size="md" label={null} />
              <span role="status">Loading…</span>
            </CardContent>
          ) : (
            <ErrorState
              error={loaded.error}
              title={editing ? 'Could not load this draft' : 'Could not load your Meta ads connection'}
              onRetry={loaded.reload}
            />
          )}
        </Card>
      </AppLayout>
    );
  }

  const { integration } = loaded.data;
  const check = integration?.lastCheck ?? null;

  /* Composing needs the account's currency, which only a successful check knows. */
  if (integration === null || check === null) {
    return (
      <AppLayout title={title} actions={<BackToMetaAds />}>
        <div data-role="meta-ad-composer-blocked" className="flex flex-wrap items-center gap-3">
          <StateChip state="not_set_up" label={integration === null ? 'No ad account connected' : 'Connection not checked'} />
          <Link to={integrationTabPath('meta-ads')} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            Meta ads connection
          </Link>
        </div>
      </AppLayout>
    );
  }

  const now = Date.now();
  if (record !== null && !isEditable(record, now) && !isCorrectable(record, now)) {
    return (
      <AppLayout title={title} actions={<BackToMetaAds />}>
        <div className="flex flex-wrap items-center gap-3">
          <DraftStatusBadge record={record} now={now} />
          <Link to={`/reach/ads/${record.id}`} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            Back to the draft
          </Link>
        </div>
      </AppLayout>
    );
  }

  const accountCurrency = check.adAccount.currency;
  const frozen =
    record === null
      ? frozenAtMetaNothing(0)
      : frozenAtMeta(record.draft, record.published);

  /*
    A frozen ad set's budget is the one Meta already holds, so it is drawn in
    the currency it was created in — not the account's. The two differ only
    when the connected account changed since, and in that case the amount
    cannot be retyped anyway: `formForAccount`'s emptying exists to stop
    ₹2,000.50 being saved as $2,000.50 by one press of Save, and there is no
    such press here. Drawing it blank under a label naming the wrong currency
    would hide the only figure on this page that costs money.
  */
  const currency = record !== null && frozen.adSet ? record.currency : accountCurrency;
  return (
    <AppLayout title={title} actions={<BackToMetaAds />}>
      <Composer
        key={record?.id ?? 'new'}
        integration={integration}
        currency={currency}
        timezone={check.adAccount.timezoneName}
        initial={
          record === null
            ? emptyDraftForm()
            : frozen.adSet
              ? formFromDraft(record.draft, record.currency)
              : formForAccount(record.draft, record.currency, currency)
        }
        previousBudget={
          record === null || record.currency === currency
            ? null
            : formatMetaMoney(record.draft.adSet.budget.amount, record.currency)
        }
        frozen={frozen}
        draftId={record?.id ?? null}
        version={record?.version ?? null}
      />
    </AppLayout>
  );
}

/* The drafts list is Reach's page since slice 65; "Meta ads" is its name in the sidebar. */
function BackToMetaAds() {
  return (
    <Link to="/reach/ads" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
      <ArrowLeft aria-hidden="true" className="size-4" />
      All Meta ads
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* The form                                                                    */
/* -------------------------------------------------------------------------- */

function Composer({
  integration,
  currency,
  timezone,
  initial,
  previousBudget,
  frozen,
  draftId,
  version,
}: {
  integration: MetaAdsIntegration;
  currency: string;
  timezone: string;
  initial: DraftForm;
  /** The saved budget as money in its own currency, when that is not `currency`. */
  previousBudget: string | null;
  /** Slice 52: what a publish already created at Meta, and so may not change. */
  frozen: FrozenAtMeta;
  draftId: string | null;
  version: number | null;
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState<DraftForm>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [pending, setPending] = useState(false);
  /* Bumped on every refusal, so the focus below runs once per refusal. */
  const [refusals, setRefusals] = useState(0);
  /* A refusal that marked no field, which only the alert by Save can show. */
  const [unplaced, setUnplaced] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const restrictedRef = useRef<HTMLDivElement>(null);

  const restricted = isRestricted(form);
  const wasRestricted = useRef(restricted);
  const set = (patch: Partial<DraftForm>) => {
    setForm((current) => ({ ...current, ...patch }));
  };
  const setAd = (index: number, patch: Partial<AdForm>) => {
    setForm((current) => ({
      ...current,
      ads: current.ads.map((ad, at) => (at === index ? { ...ad, ...patch } : ad)),
    }));
  };
  const error = (path: string): string | undefined => errors[path];

  /* A field fixed by a restricted category is not an error the person made —
     and the fields it fixed are shown, not changed off screen. */
  useEffect(() => {
    if (restricted) {
      setErrors((current) => {
        const next = { ...current };
        delete next['adSet.targeting.ageMin'];
        delete next['adSet.targeting.ageMax'];
        delete next['adSet.targeting.gender'];
        return next;
      });
      if (!wasRestricted.current) restrictedRef.current?.scrollIntoView({ block: 'nearest' });
    }
    wasRestricted.current = restricted;
  }, [restricted]);

  useEffect(() => {
    if (refusals === 0) return;
    const first = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]') ?? null;
    first?.focus();
    setUnplaced(first === null);
  }, [refusals]);

  async function save(): Promise<void> {
    if (pending) return;
    setFailure(null);
    const converted = requestFromForm(form, currency);
    if (!converted.ok) {
      setErrors(converted.errors);
      setRefusals((count) => count + 1);
      return;
    }
    setErrors({});
    setUnplaced(false);
    setPending(true);
    try {
      const saved =
        draftId === null || version === null
          ? await createMetaAdDraft(converted.request)
          : await updateMetaAdDraft(draftId, version, converted.request);
      /* Replaced, so Back does not reopen an empty "New Meta ad" that reads as
         lost work and invites a duplicate. */
      void navigate(`/reach/ads/${saved.id}`, { replace: true });
    } catch (thrown) {
      const fields = Object.fromEntries(
        Object.entries(apiFieldErrors(thrown))
          .filter(([key]) => key !== ROOT_FIELD)
          .map(([key, value]) => [fieldFor(key), String(value)]),
      );
      if (Object.keys(fields).length > 0) {
        setErrors(fields);
        setRefusals((count) => count + 1);
      } else {
        setFailure(requestFailure(thrown));
      }
      setPending(false);
    }
  }

  return (
    <form
      ref={formRef}
      data-role="meta-ad-composer"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
      className="flex max-w-3xl flex-col gap-6"
    >
      {failure !== null && <RequestFailureAlert failure={failure} />}

      <Section title="Campaign" locked={frozen.campaign}>
        {/* Where the campaign is created and billed — not what it runs as,
            which is the Page below. */}
        <dl>
          <Fact label="Ad account">{integration.accountName ?? <Mono>{integration.adAccountId}</Mono>}</Fact>
        </dl>
        <FormField label="Name" required error={error('campaign.name')}>
          <Input
            value={form.campaignName}
            onChange={(event) => set({ campaignName: event.target.value })}
            disabled={pending || frozen.campaign}
          />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="Objective" required error={error('campaign.objective')}>
            <Select
              value={form.objective}
              disabled={pending || frozen.campaign}
              options={META_AD_OBJECTIVE_VALUES.map((value) => ({
                value,
                label: OBJECTIVE_LABEL[value],
                /* Of these objectives, only traffic can open a chat. */
                disabled: form.destination === 'whatsapp' && value !== 'OUTCOME_TRAFFIC',
              }))}
              onValueChange={(value) => set({ objective: value as DraftForm['objective'] })}
            />
          </FormField>
          {/* Beside the objective it can change, so the change is made where it shows. */}
          <FormField label="Destination" required>
            {/* The destination sets the objective and every ad's creative
                format, both of which the campaign freezes, so it is frozen
                with the campaign rather than offering a change that rewrites
                a field beside it that may not move. */}
            <Select
              value={form.destination}
              disabled={pending || frozen.campaign}
              options={(['website', 'whatsapp'] as const).map((value) => ({ value, label: DESTINATION_LABEL[value] }))}
              onValueChange={(value) => setForm((current) => withDestination(current, value === 'whatsapp' ? 'whatsapp' : 'website'))}
            />
          </FormField>
          <FormField label="Special ad category" required error={error(CATEGORY_FIELD)}>
            {/* No default. `value={undefined}` shows the placeholder until chosen. */}
            <Select
              value={form.specialAdCategory ?? undefined}
              placeholder="Choose"
              disabled={pending || frozen.campaign}
              options={META_SPECIAL_AD_CATEGORY_VALUES.map((value) => ({ value, label: CATEGORY_LABEL[value] }))}
              onValueChange={(value) => {
                setForm((current) => withCategory(current, value as MetaSpecialAdCategory));
                setErrors((current) => {
                  const next = { ...current };
                  delete next[CATEGORY_FIELD];
                  return next;
                });
              }}
            />
          </FormField>
        </div>
      </Section>

      {/* The two fields here are frozen by different Meta objects — an ad set
          carries the promoted page, a creative carries both — so an ad set
          created before the first creative freezes the Page alone. The badge
          is the section's, one per section; which field is closed is said by
          the field being closed. */}
      <Section title="Runs as" locked={frozen.pageId || frozen.instagramAccountId}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Facebook Page id" required error={error('pageId')}>
            <Input
              value={form.pageId}
              inputMode="numeric"
              className="font-mono"
              onChange={(event) => set({ pageId: event.target.value })}
              disabled={pending || frozen.pageId}
            />
          </FormField>
          <FormField label="Instagram account id" error={error('instagramAccountId')}>
            <Input
              value={form.instagramAccountId}
              inputMode="numeric"
              className="font-mono"
              onChange={(event) => set({ instagramAccountId: event.target.value })}
              disabled={pending || frozen.instagramAccountId}
            />
          </FormField>
        </div>
      </Section>

      <Section title="Ad set" locked={frozen.adSet}>
        <FormField label="Name" required error={error('adSet.name')}>
          <Input value={form.adSetName} onChange={(event) => set({ adSetName: event.target.value })} disabled={pending || frozen.adSet} />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="Budget" required>
            <Select
              value={form.budgetKind}
              disabled={pending || frozen.adSet}
              options={[
                { value: 'daily', label: BUDGET_KIND_LABELS.daily },
                { value: 'lifetime', label: BUDGET_KIND_LABELS.lifetime },
              ]}
              onValueChange={(value) => set({ budgetKind: value === 'lifetime' ? 'lifetime' : 'daily' })}
            />
          </FormField>
          <FormField
            label={`Amount, ${currency}`}
            required
            description={previousBudget === null ? undefined : `Was ${previousBudget}`}
            error={error('adSet.budget.amount')}
          >
            <Input
              data-role="meta-ad-budget-amount"
              value={form.budgetAmount}
              inputMode="decimal"
              className="font-mono"
              onChange={(event) => set({ budgetAmount: event.target.value })}
              disabled={pending || frozen.adSet}
            />
          </FormField>
          <FormField label="Ends" required={form.budgetKind === 'lifetime'} error={error('adSet.endsOn')}>
            <Input
              type="date"
              min={todayIn(timezone)}
              value={form.endsOn}
              onChange={(event) => set({ endsOn: event.target.value })}
              disabled={pending || frozen.adSet}
            />
          </FormField>
        </div>

        <FormField label="Countries" required error={error('adSet.targeting.countries')}>
          <Input
            value={form.countries}
            className="font-mono uppercase"
            onChange={(event) => set({ countries: event.target.value })}
            disabled={pending || frozen.adSet}
          />
          {/* Slice 66: the codes the field holds, as the Blueprint's value
              chips, read from the same parser the request uses — so what is
              drawn is what would be sent. Read-only: the field above is still
              where a country is added or removed. */}
          {countryCodes(form.countries).length > 0 && (
            <ul aria-label="Countries this ad set targets" className="mt-2 flex flex-wrap gap-1.5">
              {countryCodes(form.countries).map((code, index) => (
                <li key={`${code}-${String(index)}`}>
                  <ValueChip label={code} className="font-mono" />
                </li>
              ))}
            </ul>
          )}
        </FormField>

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="Youngest" error={error('adSet.targeting.ageMin')}>
            <Input
              type="number"
              min={18}
              max={65}
              value={form.ageMin}
              onChange={(event) => set({ ageMin: event.target.value })}
              disabled={pending || restricted || frozen.adSet}
            />
          </FormField>
          <FormField label="Oldest" error={error('adSet.targeting.ageMax')}>
            <Input
              type="number"
              min={18}
              max={65}
              value={form.ageMax}
              onChange={(event) => set({ ageMax: event.target.value })}
              disabled={pending || restricted || frozen.adSet}
            />
          </FormField>
          <FormField label="Gender" error={error('adSet.targeting.gender')}>
            <Select
              value={form.gender}
              disabled={pending || restricted || frozen.adSet}
              options={[
                { value: 'all', label: 'Everyone' },
                { value: 'male', label: 'Men' },
                { value: 'female', label: 'Women' },
              ]}
              onValueChange={(value) => set({ gender: value as DraftForm['gender'] })}
            />
          </FormField>
        </div>
        {restricted && (
          <div ref={restrictedRef} data-role="meta-ad-restricted">
            <Badge tone="neutral">
              <Lock aria-hidden="true" className="mr-1 size-3" />
              Fixed by category
            </Badge>
          </div>
        )}

        <FormField label="Placements" error={error('adSet.targeting.placements')}>
          <div className="flex flex-col gap-2" data-role="meta-ad-placements">
            <Checkbox
              checked={form.automaticPlacements}
              onCheckedChange={(checked) => set({ automaticPlacements: checked })}
              label="Automatic"
              disabled={pending || frozen.adSet}
            />
            {!form.automaticPlacements && (
              <div className="flex flex-wrap gap-4 pl-6">
                <Checkbox checked={form.facebook} onCheckedChange={(checked) => set({ facebook: checked })} label="Facebook" disabled={pending || frozen.adSet} />
                <Checkbox
                  checked={form.instagram}
                  onCheckedChange={(checked) => setForm((current) => withInstagram(current, checked))}
                  label="Instagram"
                  disabled={pending || frozen.adSet}
                />
                {/* Slice 47: Status takes only chat ads, and runs beside Instagram Stories. */}
                <Checkbox
                  checked={form.whatsappStatus}
                  onCheckedChange={(checked) => setForm((current) => withStatus(current, checked))}
                  label="WhatsApp Status"
                  disabled={pending || frozen.adSet || form.destination !== 'whatsapp'}
                />
              </div>
            )}
          </div>
        </FormField>
      </Section>

      {/*
        REMOVAL IS WITHDRAWN FROM EVERY AD ONCE ANYTHING EXISTS AT META, not
        only from the ads that exist. `published` holds videoIds, creativeIds
        and adIds as three arrays positional against `draft.ads`; dropping any
        ad would shift every ad after it, and `creativeIds[i]` would then name
        a creative belonging to a different ad — which nothing on screen could
        show, until the resume created an ad against the wrong creative. So
        while a campaign is on file the list may be appended to and nothing
        else, and the append extends the three arrays with nulls.
      */}
      {form.ads.map((ad, index) => (
        <AdEditor
          key={index}
          index={index}
          ad={ad}
          chat={form.destination === 'whatsapp'}
          pending={pending}
          frozen={frozen.ads[index] ?? false}
          error={(path) => error(`ads.${String(index)}.${path}`)}
          onChange={(patch) => setAd(index, patch)}
          onRemove={
            !frozen.any && form.ads.length > 1
              ? () => set({ ads: form.ads.filter((_, at) => at !== index) })
              : null
          }
        />
      ))}

      {unplaced && Object.keys(errors).length > 0 && <FormAlert tone="error">Some fields need attention.</FormAlert>}

      <div className="flex flex-wrap items-center gap-3">
        {form.ads.length < META_DRAFT_MAX_ADS && (
          <Button type="button" variant="secondary" size="sm" disabled={pending} onClick={() => set({ ads: [...form.ads, emptyAd()] })}>
            <Plus aria-hidden="true" className="size-4" />
            Add an ad
          </Button>
        )}
        <Button type="submit" size="sm" loading={pending}>
          {draftId === null ? 'Save draft' : 'Save changes'}
        </Button>
        <Link
          to={draftId === null ? '/reach/ads' : `/reach/ads/${draftId}`}
          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
        >
          Cancel
        </Link>
      </div>
    </form>
  );
}

function AdEditor({
  index,
  ad,
  chat,
  pending,
  frozen,
  error,
  onChange,
  onRemove,
}: {
  index: number;
  ad: AdForm;
  /** Slice 47: the ad opens a WhatsApp chat — no link, no button, no carousel. */
  chat: boolean;
  pending: boolean;
  /** Slice 52: this ad's video, creative or ad already exists at Meta. */
  frozen: boolean;
  error: (path: string) => string | undefined;
  onChange: (patch: Partial<AdForm>) => void;
  onRemove: (() => void) | null;
}) {
  const number = String(index + 1);
  /* One flag for every input in the ad: an ad is frozen whole, because its
     creative is one object at Meta and half of it cannot be replaced. */
  const shut = pending || frozen;
  const copy = (
    <FormField label="Primary text" required error={error('creative.primaryText')}>
      <Textarea rows={3} value={ad.primaryText} onChange={(event) => onChange({ primaryText: event.target.value })} disabled={shut} />
    </FormField>
  );
  const cta = (
    <FormField label="Button">
      <Select
        value={ad.cta}
        disabled={shut}
        options={META_CTA_VALUES.map((value) => ({ value, label: CTA_LABEL[value] }))}
        onValueChange={(value) => onChange({ cta: value as AdForm['cta'] })}
      />
    </FormField>
  );
  const headlineAndLink = (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField label="Headline" required error={error('creative.headline')}>
        <Input value={ad.headline} onChange={(event) => onChange({ headline: event.target.value })} disabled={shut} />
      </FormField>
      {!chat && (
        <FormField label="Link" required error={error('creative.link')}>
          <Input value={ad.link} inputMode="url" onChange={(event) => onChange({ link: event.target.value })} disabled={shut} />
        </FormField>
      )}
      <FormField label="Description" error={error('creative.description')}>
        <Input value={ad.description} onChange={(event) => onChange({ description: event.target.value })} disabled={shut} />
      </FormField>
      {!chat && cta}
    </div>
  );

  return (
    <Section
      title={`Ad ${number}`}
      locked={frozen}
      aside={
        onRemove === null ? null : (
          <Button type="button" variant="ghost" size="sm" disabled={shut} onClick={onRemove} aria-label={`Remove ad ${number}`}>
            <Trash2 aria-hidden="true" className="size-4" />
            Remove
          </Button>
        )
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Name" required error={error('name')}>
          <Input value={ad.name} onChange={(event) => onChange({ name: event.target.value })} disabled={shut} />
        </FormField>
        <FormField label="Format" required error={error('creative.format')}>
          <Select
            value={ad.format}
            disabled={shut}
            options={(['single_image', 'carousel', 'single_video'] as const).map((value) => ({
              value,
              label: FORMAT_LABEL[value],
              disabled: chat && value === 'carousel',
            }))}
            onValueChange={(value) => onChange({ format: value as CreativeFormat })}
          />
        </FormField>
      </div>

      {ad.format === 'single_image' && (
        <>
          <FormField label="Image address" required error={error('creative.imageUrl')}>
            <Input value={ad.imageUrl} inputMode="url" onChange={(event) => onChange({ imageUrl: event.target.value })} disabled={shut} />
          </FormField>
          {copy}
          {headlineAndLink}
        </>
      )}

      {ad.format === 'single_video' && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Video address" required error={error('creative.videoUrl')}>
              <Input value={ad.videoUrl} inputMode="url" onChange={(event) => onChange({ videoUrl: event.target.value })} disabled={shut} />
            </FormField>
            <FormField label="Thumbnail address" required error={error('creative.thumbnailUrl')}>
              <Input value={ad.thumbnailUrl} inputMode="url" onChange={(event) => onChange({ thumbnailUrl: event.target.value })} disabled={shut} />
            </FormField>
          </div>
          {copy}
          {headlineAndLink}
        </>
      )}

      {ad.format === 'carousel' && (
        <>
          {copy}
          {!chat && cta}
          <ol data-role="meta-ad-cards" className="flex flex-col gap-3">
            {ad.cards.map((card, at) => {
              const cardNumber = String(at + 1);
              const setCard = (patch: Partial<AdForm['cards'][number]>) =>
                onChange({ cards: ad.cards.map((one, i) => (i === at ? { ...one, ...patch } : one)) });
              return (
                <li key={at} className="flex flex-col gap-3 rounded-base border border-line p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge tone="neutral">Card {cardNumber}</Badge>
                    {ad.cards.length > 2 && !frozen && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={shut}
                        aria-label={`Remove card ${cardNumber} of ad ${number}`}
                        onClick={() => onChange({ cards: ad.cards.filter((_, other) => other !== at) })}
                      >
                        <Trash2 aria-hidden="true" className="size-4" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField label="Image address" required error={error(`creative.cards.${String(at)}.imageUrl`)}>
                      <Input value={card.imageUrl} inputMode="url" onChange={(event) => setCard({ imageUrl: event.target.value })} disabled={shut} />
                    </FormField>
                    {!chat && (
                      <FormField label="Link" required error={error(`creative.cards.${String(at)}.link`)}>
                        <Input value={card.link} inputMode="url" onChange={(event) => setCard({ link: event.target.value })} disabled={shut} />
                      </FormField>
                    )}
                    <FormField label="Headline" required error={error(`creative.cards.${String(at)}.headline`)}>
                      <Input value={card.headline} onChange={(event) => setCard({ headline: event.target.value })} disabled={shut} />
                    </FormField>
                    <FormField label="Description" error={error(`creative.cards.${String(at)}.description`)}>
                      <Input value={card.description} onChange={(event) => setCard({ description: event.target.value })} disabled={shut} />
                    </FormField>
                  </div>
                </li>
              );
            })}
          </ol>
          {ad.cards.length < 10 && !frozen && (
            <div>
              <Button type="button" variant="secondary" size="sm" disabled={shut} onClick={() => onChange({ cards: [...ad.cards, emptyCard()] })}>
                <Plus aria-hidden="true" className="size-4" />
                Add a card
              </Button>
            </div>
          )}
          {error('creative.cards') !== undefined && (
            <FormAlert tone="error">{error('creative.cards')}</FormAlert>
          )}
        </>
      )}
    </Section>
  );
}

/**
 * A titled block of the form, with the composer's own lock badge when what it
 * holds already exists at Meta.
 *
 * One badge per section, never per field — the pattern "Fixed by category"
 * already set in the ad set. Two words, written once here and used by every
 * section, because the alternative was a sentence under each explaining why
 * half the form is closed; the draft page the person arrived from already
 * draws exactly which ids exist, so the badge only has to name the reason.
 */
function Section({
  title,
  aside,
  locked = false,
  children,
}: {
  title: string;
  aside?: ReactNode;
  locked?: boolean;
  children: ReactNode;
}) {
  return (
    <Card data-role="meta-ad-composer-section" data-locked={locked ? 'true' : 'false'}>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <span className="flex flex-wrap items-center gap-2">
          <CardTitle>{title}</CardTitle>
          {locked && (
            <Badge tone="neutral" data-role="meta-ad-at-meta">
              <Lock aria-hidden="true" className="mr-1 size-3" />
              At Meta
            </Badge>
          )}
        </span>
        {aside}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{children}</CardContent>
    </Card>
  );
}
